import * as PrefabManager from '../PrefabManager';
import { BaseVehicle } from './BaseVehicle';

import {Humanoid} from '../humanoids/Humanoid';
import { SkateHelmet } from '../hats/skateHelmet'
import * as AudioManager from '../../utils/AudioManager';
import * as emitterManager from '../../utils/EmitterManager';
import { simulationFrame } from '../../utils/SimulationClock';
import { Settings } from '../../Settings';


import {
    game
} from "../../Game";

const { getPointer, NULL } = Box2D;

const vec1 = new Box2D.b2Vec2();
const worldManifold = new Box2D.b2WorldManifold();

// Poses are in deck space while facing right: x towards the nose, y down, angles clockwise.
const STANCE = {
    back: {x: -1.0, y: -0.21},
    front: {x: 0.95, y: -0.22},
    tail: {x: -1.5, y: -0.21},
    nose: {x: 1.45, y: -0.22},
};
const LEGS = {back: 'left', front: 'right'};
const ARMS = {
    ride: {left: [20, 30], right: [8, 25]},
    crouch: {left: [60, 75], right: [45, 85]},
    pop: {left: [75, 25], right: [30, 20]},
    air: {left: [70, 20], right: [-10, 20]},
    land: {left: [45, 40], right: [25, 35]},
    speed: {left: [-15, 55], right: [-10, 65]},
    brake: {left: [55, 30], right: [15, 40]},
    tail: {left: [-15, 25], right: [100, 20]},
    nose: {left: [100, 20], right: [-15, 25]},
};
const GRIPS = {back: 'grip_back', front: 'grip_front'};
const ANKLE_FLEX = 0.9;
const PUSH_ANKLE_FLEX = 1.25;
const GRIP = {force: 900, torque: 60, correction: 0.3, slip: 0.8, release: 0.6};
// Motor torques stay well below the 600 that snaps a limb.
// Arm motors shake at the full gain.
const MOTOR = {gain: 12, armGain: 5, maxSpeed: 12, hip: 160, knee: 160, ankle: 50, shoulder: 105, elbow: 54, wrist: 12, head: 40,
    limpTime: 400, stretch: 0.06};
const POSE = {rideCrouch: 0.15, rideLean: 0.08, crouchLean: 0.35, speedCrouch: 0.7, speedLean: 0.45, brakeCrouch: 0.85, brakeLean: -0.1, leanSpeed: 0.002};
const PUSH = {grip: 0.35, cycle: 900, minCycle: 500, plant: 0.28, tuckSpeed: 22, untuckSpeed: 18, brakeSpeed: 2.5,
    gripRamp: 250, touchdown: 0.32, friction: 0.05,
    forwards: {stroke: 2.55, shift: 0, lean: 0.2, path: [[0, 0.95, -0.22], [0.16, 1.5, -0.6], [0.32, 1.3, 0.7], [0.6, -1.25, 0.7], [0.72, -1.75, 0.25], [0.84, -1.6, -0.35], [0.93, -0.4, -0.75]]},
    backwards: {stroke: 1.6, shift: 0.45, lean: 0.05, path: [[0, 0.95, -0.22], [0.15, 0.45, -0.55], [0.3, 0.05, 0.7], [0.62, 1.65, 0.7], [0.75, 1.9, 0.05], [0.88, 1.5, -0.6]]},
    brake: {x: 0.55, y: 0.7}};
// Every move of the rider rocks the deck, so it is held to the surface while it rolls on it (not in the air or at edges).
const STEADY = {time: 250, window: 0.17, stiffness: 800, damping: 57, maxAccel: 250};
const CROUCH = {max: 1.3, tuck: 0.8, fall: 0.35, extend: -0.25, push: 1.05, speed: 0.012, popSpeed: 0.03, landTime: 300};
const JUMP = {chargeTime: 600, coyoteTime: 120, minSpeed: 4.5, maxSpeed: 9, spin: 0.45, noseUp: 0.7,
    popTime: 150, levelTime: 450, levelStiffness: 40, levelDamping: 10, upright: 0.5};
// A grind is a deck end on, or just above, the surface its wheel rides on while the other wheel is up, after leaning that way.
const GRIND = {leanFrames: 45, reach: 0.15, keepReach: 0.35, endZone: 0.7, hold: 100, blendIn: 0.008, blendOut: 0.005, shift: 0.6, upright: {tail: 0.25, nose: 0.5}, crouch: 0.5,
    stiffness: 90, damping: 18, maxAccel: 45, overTilt: 0.4,
    friction: 0.05, tailFront: {x: 0.35, y: -0.22}, noseBack: {x: -0.35, y: -0.21}, sparkSpeed: 2, sound: 'skateboard_grind_loop'};
// A hard hit on the nose or tail throws the rider off. The deck stops a step before the heavier wheels and the grips
// slow the rider meanwhile, so hits are summed over a few steps and the rider keeps the speed from before.
const BAIL = {speed: 12, steps: 3, axis: 0.6};

const DEG = Math.PI / 180;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const lerp = (a, b, t) => ({x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t});
const lerpArms = (a, b, t) => ({
    left: [a.left[0] + (b.left[0] - a.left[0]) * t, a.left[1] + (b.left[1] - a.left[1]) * t],
    right: [a.right[0] + (b.right[0] - a.right[0]) * t, a.right[1] + (b.right[1] - a.right[1]) * t],
});
const easeOut = t => 1 - (1 - t) * (1 - t);
const wrapAngle = angle => {
    angle %= Math.PI * 2;
    if (angle > Math.PI) angle -= Math.PI * 2;
    else if (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
};
// Catmull-Rom through a closed loop of [phase, x, y] keys.
const loopPoint = (keys, phase) => {
    phase -= Math.floor(phase);
    let i = keys.length - 1;
    while (keys[i][0] > phase) i--;
    const key = n => keys[(n + keys.length) % keys.length];
    const start = keys[i][0];
    const end = i + 1 < keys.length ? keys[i + 1][0] : 1;
    const t = (phase - start) / (end - start);
    const [p0, p1, p2, p3] = [key(i - 1), key(i), key(i + 1), key(i + 2)];
    const spline = k => 0.5 * (2 * p1[k] + (-p0[k] + p2[k]) * t + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t * t + (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t * t * t);
    return {x: spline(1), y: spline(2)};
};

class Skateboard extends BaseVehicle {
    constructor(target) {
        super(target);
        const riderJoints = ['neck_stabalize_joint', 'back_stabalize_joint', 'back_stand_joint', 'front_stand_joint', 'foot_left_d1', 'foot_left_rope', 'foot_left_d2', 'grip_back', 'grip_front'];
        this.destroyConnectedJoints = {
            head:riderJoints,
            body:riderJoints,
            thigh_left:riderJoints,
            thigh_right:riderJoints,
            leg_left:riderJoints,
            leg_right:riderJoints,
            feet_left:riderJoints,
            feet_right:riderJoints,
            shoulder_left:[],
            shoulder_right:[],
            arm_left:[],
            arm_right:[],
            hand_left:[],
            hand_right:[],
        }
        this.vehicleName = 'Skateboard';
        this.canJump = true;
        this.limbsObserver = [Humanoid.BODY_PARTS.BELLY, Humanoid.BODY_PARTS.FEET_RIGHT, Humanoid.BODY_PARTS.FEET_LEFT, Humanoid.BODY_PARTS.LEG_RIGHT, Humanoid.BODY_PARTS.LEG_LEFT, Humanoid.BODY_PARTS.THIGH_RIGHT, Humanoid.BODY_PARTS.THIGH_LEFT];
    }
    init() {
		super.init();
        this.leanSpeed = 0.3;
        this.desiredVehicleTorques = [200, 200];
        this.desiredVehicleSpeeds = [20, 20];

        this.initRider();
        this.character.setHat(SkateHelmet);

        this.sparks = emitterManager.getLoopingEmitter('grindSparks', this.lookupObject.frame, this.lookupObject.frame.GetPosition(), 0);
        this.sparks.emit = false;
        AudioManager.preloadSFX(GRIND.sound);
    }
    initRider() {
        // The rider is initialised after us and still holds the editor's lookup.
        const rider = this.character.lookupObject = game.editor.lookupGroups[this.lookupObject.character.body.mySprite.data.subPrefabInstanceName];
        ['foot_left_d1', 'foot_left_d2'].forEach(name => game.editor.DestroyJoint(rider[name]));

        rider[GRIPS.back] = this.createGrip(rider[Humanoid.BODY_PARTS.FEET_LEFT]);
        rider[GRIPS.front] = this.createGrip(rider[Humanoid.BODY_PARTS.FEET_RIGHT]);
        this.setAnkleFlex(ANKLE_FLEX);

        const frame = this.lookupObject.frame;
        const copy = vector => ({x: vector.x, y: vector.y});
        const local = body => copy(frame.GetLocalPoint(body.GetPosition()));

        this.supportAnchors = [
            [rider, 'back_stand_joint'], [rider, 'front_stand_joint'], [this.lookupObject, 'stabalizer_joint'],
        ].map(([lookup, name]) => ({lookup, name, ...copy(lookup[name].GetLocalAnchorB())}));
        this.hips = local(rider[Humanoid.BODY_PARTS.BELLY]);

        this.legs = {};
        Object.entries(LEGS).forEach(([foot, side]) => {
            const hip = rider[`thigh_${side}_joint`];
            const knee = rider[`leg_${side}_joint`];
            const ankle = rider[`feet_${side}_joint`];
            const thigh = {x: knee.GetLocalAnchorB().x - hip.GetLocalAnchorA().x, y: knee.GetLocalAnchorB().y - hip.GetLocalAnchorA().y};
            const shin = {x: ankle.GetLocalAnchorB().x - knee.GetLocalAnchorA().x, y: ankle.GetLocalAnchorB().y - knee.GetLocalAnchorA().y};
            this.legs[foot] = {side, thigh, shin, hipOnBelly: copy(hip.GetLocalAnchorB()), ankleOnFoot: copy(ankle.GetLocalAnchorA()),
                thighLength: Math.hypot(thigh.x, thigh.y), shinLength: Math.hypot(shin.x, shin.y)};
        });

        this.pivots = {tail: local(this.lookupObject.wheel_back), nose: local(this.lookupObject.wheel_front)};
        const deck = Box2D.castObject(frame.GetFixtureList().GetShape(), Box2D.b2PolygonShape);
        const corners = {tail: {x: Infinity, y: -Infinity}, nose: {x: -Infinity, y: -Infinity}};
        for (let i = 0; i < deck.get_m_count(); i++) {
            const vertex = deck.get_m_vertices(i);
            corners.tail.x = Math.min(corners.tail.x, vertex.x);
            corners.nose.x = Math.max(corners.nose.x, vertex.x);
            corners.tail.y = corners.nose.y = Math.max(corners.tail.y, vertex.y);
        }
        this.deckCorners = corners;
        // Tilt at which each deck end touches the ground while its wheel still does.
        const radius = this.lookupObject.wheel_back.GetFixtureList().GetShape().get_m_radius();
        this.grindAngles = {};
        ['tail', 'nose'].forEach(side => {
            const x = Math.abs(corners[side].x - this.pivots[side].x);
            const y = this.pivots[side].y - corners[side].y;
            this.grindAngles[side] = Math.asin(Math.min(1, radius / Math.hypot(x, y))) + Math.atan2(y, x);
        });
        this.boardInertia = frame.GetInertia();
        this.boardMass = frame.GetMass();
        [this.lookupObject.wheel_back, this.lookupObject.wheel_front].forEach(wheel => {
            this.boardMass += wheel.GetMass();
            const dx = wheel.GetWorldCenter().x - frame.GetWorldCenter().x;
            const dy = wheel.GetWorldCenter().y - frame.GetWorldCenter().y;
            this.boardInertia += wheel.GetInertia() + wheel.GetMass() * (dx * dx + dy * dy);
        });

        this.jumpHeld = false;
        this.charge = 0;
        this.popTime = -1;
        this.takeoffAngle = 0;
        this.crouch = 0;
        this.torsoLean = 0;
        this.airTime = 0;
        this.rollTime = Infinity;
        this.landTime = -1;
        this.landDepth = 0;
        this.riderFrame = -1;
        this.leanFrame = -1;
        this.pushPhase = 0;
        this.grindSide = 'tail';
        this.grind = 0;
        this.grindHold = Infinity;
        this.grindSpot = null;
        this.leftGrind = null;
        this.leanFrames = {tail: -Infinity, nose: -Infinity};
        this.groundAngle = frame.GetAngle();
        this.wheelGround = {back: null, front: null};
        this.deckEnds = {tail: null, nose: null};
        this.frontGrip = 1;
        this.tucked = false;
        this.braking = false;
        this.pushShift = 0;
        this.armLimp = {left: 1, right: 1};
        this.bailImpulse = 0;
        this.bailHits = [];
        this.bailVelocity = {x: 0, y: 0};
        this.slide = null;
        this.feet = {back: STANCE.back, front: STANCE.front};
        this.applyGrips();
    }
    createGrip(foot) {
        const def = new Box2D.b2MotorJointDef();
        def.Initialize(this.lookupObject.frame, foot);
        def.set_maxForce(GRIP.force);
        def.set_maxTorque(GRIP.torque);
        def.set_correctionFactor(GRIP.correction);
        const joint = Box2D.castObject(game.editor.CreateJoint(def), Box2D.b2MotorJoint);
        Box2D.destroy(def);
        return joint;
    }
    grip(side) {
        const rider = this.character.lookupObject;
        const joint = rider[GRIPS[side]];
        if (joint && joint.destroyed) {
            delete rider[GRIPS[side]];
            return null;
        }
        return joint;
    }
    joint(name) {
        const joint = this.character.lookupObject[name];
        return joint && !joint.destroyed ? joint : null;
    }
    setAnkleFlex(flex) {
        ['feet_left_joint', 'feet_right_joint'].forEach(name => this.joint(name)?.SetLimits(-flex, flex));
    }
    applyGrips() {
        const s = this.flipped ? -1 : 1;
        [['back', this.feet.back, 1], ['front', this.feet.front, this.frontGrip]].forEach(([side, target, strength]) => {
            const joint = this.grip(side);
            if (!joint) return;
            vec1.Set(target.x * s, target.y);
            joint.SetLinearOffset(vec1);
            joint.SetAngularOffset(0);
            const local = this.lookupObject.frame.GetLocalPoint(joint.GetBodyB().GetPosition());
            const offset = Math.hypot(local.x - vec1.x, local.y - vec1.y);
            strength *= clamp(1 - (offset - GRIP.slip) / GRIP.release, 0, 1);
            joint.SetMaxForce(GRIP.force * strength);
            joint.SetMaxTorque(GRIP.torque * strength);
        });
    }

    setSupport(depth, shift, lean, tilt, pivot) {
        const s = this.flipped ? -1 : 1;
        const leanCos = Math.cos(lean);
        const leanSin = Math.sin(lean);
        const tiltCos = Math.cos(-tilt);
        const tiltSin = Math.sin(-tilt);
        const hipX = this.hips.x + shift;
        const hipY = this.hips.y + depth;
        this.supportAnchors.forEach(anchor => {
            const joint = anchor.lookup[anchor.name];
            if (!joint || joint.destroyed) return;
            let x = anchor.x + shift - hipX;
            let y = anchor.y + depth - hipY;
            [x, y] = [hipX + x * leanCos - y * leanSin - pivot.x, hipY + x * leanSin + y * leanCos - pivot.y];
            vec1.Set(s * (pivot.x + x * tiltCos - y * tiltSin), pivot.y + x * tiltSin + y * tiltCos);
            joint.GetLocalAnchorB().Set(vec1.x, vec1.y);
        });
    }
    drive(name, target, torque, gain = MOTOR.gain) {
        const joint = this.joint(name);
        if (!joint) return;
        target = clamp(target, joint.GetLowerLimit(), joint.GetUpperLimit());
        joint.EnableMotor(true);
        joint.SetMaxMotorTorque(torque);
        joint.SetMotorSpeed(clamp(wrapAngle(target - joint.GetJointAngle()) * gain, -MOTOR.maxSpeed, MOTOR.maxSpeed));
    }
    releaseMotors() {
        ['thigh_left', 'leg_left', 'feet_left', 'thigh_right', 'leg_right', 'feet_right', 'shoulder_left', 'arm_left', 'hand_left',
            'shoulder_right', 'arm_right', 'hand_right', 'head'].forEach(part => this.joint(`${part}_joint`)?.EnableMotor(false));
    }
    hipPoint(leg) {
        const belly = this.character.lookupObject[Humanoid.BODY_PARTS.BELLY];
        if (!belly) return null;
        vec1.Set((this.flipped ? -1 : 1) * leg.hipOnBelly.x, leg.hipOnBelly.y);
        const hip = belly.GetWorldPoint(vec1);
        return {x: hip.x, y: hip.y};
    }
    ankleOffset(leg, footAngle) {
        const x = (this.flipped ? -1 : 1) * leg.ankleOnFoot.x;
        const y = leg.ankleOnFoot.y;
        return {x: Math.cos(footAngle) * x - Math.sin(footAngle) * y, y: Math.sin(footAngle) * x + Math.cos(footAngle) * y};
    }
    withinReach(foot, point, footAngle) {
        const leg = this.legs[foot];
        const hip = this.hipPoint(leg);
        if (!hip) return point;
        const offset = this.ankleOffset(leg, footAngle);
        const x = point.x + offset.x - hip.x;
        const y = point.y + offset.y - hip.y;
        const reach = Math.hypot(x, y);
        const longest = (leg.thighLength + leg.shinLength) * 0.97;
        if (reach <= longest) return point;
        return {x: hip.x + x * longest / reach - offset.x, y: hip.y + y * longest / reach - offset.y};
    }
    driveLeg(foot, footX, footY, footAngle) {
        const leg = this.legs[foot];
        const belly = this.character.lookupObject[Humanoid.BODY_PARTS.BELLY];
        const hip = this.hipPoint(leg);
        if (!hip || !this.joint(`thigh_${leg.side}_joint`)) return;
        const s = this.flipped ? -1 : 1;
        const hipX = hip.x;
        const hipY = hip.y;
        const offset = this.ankleOffset(leg, footAngle);
        const ankleX = footX + offset.x;
        const ankleY = footY + offset.y;
        const thighLength = leg.thighLength;
        const shinLength = leg.shinLength;
        const reach = clamp(Math.hypot(ankleX - hipX, ankleY - hipY), Math.abs(thighLength - shinLength) + 0.01, thighLength + shinLength - 0.001);
        const direction = Math.atan2(ankleY - hipY, ankleX - hipX);
        const bend = Math.acos(clamp((thighLength * thighLength + reach * reach - shinLength * shinLength) / (2 * thighLength * reach), -1, 1));
        const forward = this.lookupObject.frame.GetAngle() + (this.flipped ? Math.PI : 0);
        const thighDirection = Math.cos(direction - bend - forward) > Math.cos(direction + bend - forward) ? direction - bend : direction + bend;
        const kneeX = hipX + thighLength * Math.cos(thighDirection);
        const kneeY = hipY + thighLength * Math.sin(thighDirection);
        const shinDirection = Math.atan2(hipY + reach * Math.sin(direction) - kneeY, hipX + reach * Math.cos(direction) - kneeX);
        const thighAngle = thighDirection - Math.atan2(leg.thigh.y, s * leg.thigh.x);
        const shinAngle = shinDirection - Math.atan2(leg.shin.y, s * leg.shin.x);
        this.drive(`thigh_${leg.side}_joint`, belly.GetAngle() - thighAngle, MOTOR.hip);
        this.drive(`leg_${leg.side}_joint`, thighAngle - shinAngle, MOTOR.knee);
        this.drive(`feet_${leg.side}_joint`, shinAngle - footAngle, MOTOR.ankle);
    }
    bumped(body) {
        if (!body) return false;
        for (let edge = body.GetContactList(); getPointer(edge) !== getPointer(NULL); edge = edge.get_next()) {
            const contact = edge.contact;
            if (contact.IsTouching() && contact.IsEnabled() && !edge.other.mainCharacter && !contact.GetFixtureA().IsSensor() && !contact.GetFixtureB().IsSensor()) return true;
        }
        return false;
    }
    stretched(name) {
        const joint = this.joint(name);
        if (!joint) return false;
        const anchorA = joint.GetAnchorA();
        const x = anchorA.x;
        const y = anchorA.y;
        const anchorB = joint.GetAnchorB();
        return Math.hypot(x - anchorB.x, y - anchorB.y) > MOTOR.stretch;
    }
    driveArms(arms, headPitch) {
        const s = this.flipped ? -1 : 1;
        const rider = this.character.lookupObject;
        ['left', 'right'].forEach(side => {
            const parts = [`shoulder_${side}`, `arm_${side}`, `hand_${side}`];
            if (parts.some(part => this.bumped(rider[part]) || this.stretched(`${part}_joint`))) this.armLimp[side] = 0;
            else this.armLimp[side] = Math.min(1, this.armLimp[side] + game.editor.deltaTime / MOTOR.limpTime);
            const firm = this.armLimp[side];
            this.drive(`shoulder_${side}_joint`, s * arms[side][0] * DEG, MOTOR.shoulder * firm, MOTOR.armGain);
            this.drive(`arm_${side}_joint`, s * arms[side][1] * DEG, MOTOR.elbow * firm, MOTOR.armGain);
            this.drive(`hand_${side}_joint`, 0, MOTOR.wrist * firm, MOTOR.armGain);
        });
        const body = this.character.lookupObject[Humanoid.BODY_PARTS.BODY];
        if (body) this.drive('head_joint', body.GetAngle() - this.groundAngle - s * headPitch, MOTOR.head);
    }
    updateGround() {
        let normalX = 0;
        let normalY = 0;
        const touching = wheel => {
            if (!wheel || wheel.snapped) return null;
            for (let edge = wheel.GetContactList(); getPointer(edge) !== getPointer(NULL); edge = edge.get_next()) {
                const contact = edge.contact;
                if (!contact.IsTouching() || !contact.IsEnabled() || !contact.GetManifold().get_pointCount()) continue;
                if (contact.GetFixtureA().IsSensor() || contact.GetFixtureB().IsSensor()) continue;
                if (edge.other.mainCharacter) continue;
                contact.GetWorldManifold(worldManifold);
                const normal = worldManifold.get_normal();
                const towardsWheel = contact.GetFixtureA().GetBody() === wheel ? -1 : 1;
                const point = worldManifold.get_points(0);
                const ground = {x: point.x, y: point.y, nx: normal.x * towardsWheel, ny: normal.y * towardsWheel, body: edge.other};
                normalX += ground.nx;
                normalY += ground.ny;
                return ground;
            }
            return null;
        };
        this.wheelGround = {back: touching(this.lookupObject.wheel_back), front: touching(this.lookupObject.wheel_front)};
        if (this.wheelGround.back || this.wheelGround.front) this.groundAngle = Math.atan2(normalX, -normalY);
        return Boolean(this.wheelGround.back || this.wheelGround.front);
    }
    updateDeck() {
        const frame = this.lookupObject.frame;
        const s = this.flipped ? -1 : 1;
        this.deckEnds = {tail: null, nose: null};
        this.scrape = null;
        let fastest = GRIND.sparkSpeed;
        for (let edge = frame.GetContactList(); getPointer(edge) !== getPointer(NULL); edge = edge.get_next()) {
            const contact = edge.contact;
            if (!contact.IsTouching() || !contact.IsEnabled() || !contact.GetManifold().get_pointCount()) continue;
            if (contact.GetFixtureA().IsSensor() || contact.GetFixtureB().IsSensor()) continue;
            if (edge.other.mainCharacter) continue;
            contact.GetWorldManifold(worldManifold);
            const normal = worldManifold.get_normal();
            const towardsDeck = contact.GetFixtureA().GetBody() === frame ? -1 : 1;
            const point = worldManifold.get_points(0);
            const touch = {x: point.x, y: point.y, nx: normal.x * towardsDeck, ny: normal.y * towardsDeck, body: edge.other};
            const along = s * frame.GetLocalPoint(point).x;
            if (along < this.deckCorners.tail.x + GRIND.endZone) this.deckEnds.tail = touch;
            else if (along > this.deckCorners.nose.x - GRIND.endZone) this.deckEnds.nose = touch;
            const speed = this.slideSpeed(touch);
            if (speed > fastest) {
                fastest = speed;
                this.scrape = touch;
            }
        }
    }
    slideSpeed(touch) {
        vec1.Set(touch.x, touch.y);
        const deck = this.lookupObject.frame.GetLinearVelocityFromWorldPoint(vec1);
        const deckX = deck.x;
        const deckY = deck.y;
        const other = touch.body.GetLinearVelocityFromWorldPoint(vec1);
        touch.vx = deckX - other.x;
        touch.vy = deckY - other.y;
        return Math.hypot(touch.vx, touch.vy);
    }

    findGrind() {
        const frame = this.lookupObject.frame;
        const s = this.flipped ? -1 : 1;
        for (const side of ['tail', 'nose']) {
            if (side === this.leftGrind || this.wheelGround[side === 'tail' ? 'front' : 'back']) continue;
            const grinding = this.grind > 0 && this.grindSide === side;
            if (!grinding && simulationFrame() - this.leanFrames[side] > GRIND.leanFrames) continue;
            const touch = this.deckEnds[side];
            if (touch) {
                const tilt = s * wrapAngle(frame.GetAngle() - Math.atan2(touch.nx, -touch.ny)) * (side === 'tail' ? -1 : 1);
                if (tilt > 0 && tilt < this.grindAngles[side] + GRIND.overTilt) return {side, ...touch};
                continue;
            }
            const ground = this.wheelGround[side === 'tail' ? 'back' : 'front'];
            if (!ground) continue;
            vec1.Set(s * this.deckCorners[side].x, this.deckCorners[side].y);
            const corner = frame.GetWorldPoint(vec1);
            const height = (corner.x - ground.x) * ground.nx + (corner.y - ground.y) * ground.ny;
            const reach = this.grindHold < GRIND.hold && this.grindSide === side ? GRIND.keepReach : GRIND.reach;
            if (height < reach) return {side, ...ground, x: corner.x - height * ground.nx, y: corner.y - height * ground.ny};
        }
        return null;
    }
    updateSparks() {
        const grind = this.grind > 0.5 && this.grindHold === 0 ? this.grindSpot : null;
        const spot = grind && this.slideSpeed(grind) > GRIND.sparkSpeed ? grind : this.scrape;
        if (spot) {
            const speed = Math.hypot(spot.vx, spot.vy);
            const dirX = -spot.vx / speed + spot.nx * 0.4;
            const dirY = -spot.vy / speed + spot.ny * 0.4;
            this.sparks.spawnPos.set(spot.x * Settings.PTM, spot.y * Settings.PTM);
            this.sparks.rotation = Math.atan2(dirY, dirX) * game.editor.RAD2DEG;
            this.sparks.playOnce();
            this.slide = {speed, time: 0};
        } else if (this.slide) {
            this.slide.time += game.editor.deltaTime;
            if (this.slide.time > GRIND.hold) this.slide = null;
        }
        this.sparks.update(game.editor.deltaTime / 1000);
        if (this.slide) {
            const speed = this.slide.speed;
            AudioManager.playPrefabUniqueLoopSFX(this.prefabObject.key, GRIND.sound, clamp(speed * 0.04, 0.1, 0.5), clamp(0.8 + speed * 0.03, 0.8, 1.3), this.lookupObject.frame.GetPosition());
        } else {
            AudioManager.stopPrefabUniqueLoopSFX(this.prefabObject.key, GRIND.sound);
        }
    }
    jump(held) {
        this.jumpHeld = held;
    }
    lean(dir) {
        this.leanFrame = simulationFrame();
        const towardsTail = dir === (this.flipped ? 1 : -1);
        this.leanFrames[towardsTail ? 'tail' : 'nose'] = simulationFrame();
        if (this.grind > 0 && towardsTail !== (this.grindSide === 'tail')) this.leftGrind = this.grindSide;
        super.lean(dir);
    }
    flip() {
        super.flip();
        this.applyGrips();
    }
    pop(charge) {
        const frame = this.lookupObject.frame;
        const angle = this.airTime > 0 ? frame.GetAngle() : this.groundAngle;
        const speed = JUMP.minSpeed + (JUMP.maxSpeed - JUMP.minSpeed) * charge;
        const upX = Math.sin(angle) * speed;
        const upY = -Math.cos(angle) * speed;
        // Slow enough that the back wheels still lift.
        const spin = (this.flipped ? 1 : -1) * JUMP.spin * speed;
        const board = [frame, this.lookupObject.wheel_back, this.lookupObject.wheel_front];
        let mass = 0;
        let centerX = 0;
        let centerY = 0;
        board.forEach(body => {
            mass += body.GetMass();
            centerX += body.GetWorldCenter().x * body.GetMass();
            centerY += body.GetWorldCenter().y * body.GetMass();
        });
        centerX /= mass;
        centerY /= mass;
        this.getCurrentActiveBodies().forEach(body => {
            if (body.snapped) return;
            let vx = upX;
            let vy = upY;
            if (board.includes(body)) {
                const center = body.GetWorldCenter();
                vx -= spin * (center.y - centerY);
                vy += spin * (center.x - centerX);
            }
            const velocity = body.GetLinearVelocity();
            vec1.Set(velocity.x + vx, velocity.y + vy);
            body.SetLinearVelocity(vec1);
        });
        frame.SetAngularVelocity(frame.GetAngularVelocity() + spin);
        this.takeoffAngle = angle;
        this.noseUpAngle = (this.flipped ? 1 : -1) * JUMP.noseUp * (0.5 + charge * 0.5);
        this.popTime = 0;
        this.airControl = false;
        AudioManager.playSFX('impact-small1', 0.3 + charge * 0.3, 1.4, frame.GetPosition());
    }
    updateRider(idleVolume) {
        if (simulationFrame() === this.riderFrame) return;
        this.riderFrame = simulationFrame();

        const dt = game.editor.deltaTime;
        const frame = this.lookupObject.frame;
        const s = this.flipped ? -1 : 1;
        const grounded = this.updateGround() || Boolean(this.deckEnds.tail || this.deckEnds.nose);
        const lastAirTime = this.airTime;
        this.airTime = grounded ? 0 : this.airTime + dt;
        // Wheels skip off the ground for single frames; short gaps still count as riding on it.
        const onGround = this.airTime <= JUMP.coyoteTime;

        if (this.popTime >= 0) {
            this.popTime += dt;
            if (grounded && this.popTime > JUMP.levelTime) this.popTime = -1;
        }
        if (this.jumpHeld && this.popTime < 0) {
            if (onGround) this.charge = Math.min(1, this.charge + dt / JUMP.chargeTime);
        } else if (this.charge > 0) {
            if (onGround) this.pop(this.charge);
            this.charge = 0;
        }

        if (grounded && lastAirTime > JUMP.coyoteTime) {
            this.popTime = -1;
            this.landTime = 0;
            this.landDepth = CROUCH.max * Math.min(0.7, Math.max(0.25, lastAirTime / 900));
            AudioManager.playSFX('impact-small2', Math.min(0.5, lastAirTime / 1500), 1.2, frame.GetPosition());
        }

        if (this.wheelGround.back && this.wheelGround.front) this.leftGrind = null;
        const spot = this.popTime < 0 ? this.findGrind() : null;
        if (spot) {
            this.grindSide = spot.side;
            this.grindSpot = spot;
            this.grindHold = 0;
            this.groundAngle = Math.atan2(spot.nx, -spot.ny);
        } else {
            this.grindHold += dt;
        }
        const grinding = this.grindHold < GRIND.hold;
        this.grind = clamp(this.grind + (grinding ? GRIND.blendIn : -GRIND.blendOut) * dt, 0, 1);

        this.rollTime = this.wheelGround.back && this.wheelGround.front ? 0 : this.rollTime + dt;
        const steadyError = wrapAngle(this.groundAngle - frame.GetAngle());
        if ((this.wheelGround.back || this.wheelGround.front) && this.rollTime < STEADY.time && Math.abs(steadyError) < STEADY.window && this.leanFrame !== simulationFrame() && this.grind === 0 && this.popTime < 0) {
            const accel = STEADY.stiffness * steadyError - STEADY.damping * frame.GetAngularVelocity();
            frame.ApplyTorque(this.boardInertia * clamp(accel, -STEADY.maxAccel, STEADY.maxAccel), true);
        }

        const tail = this.grindSide === 'tail';
        if (spot && this.leanFrame !== simulationFrame() && this.popTime < 0) {
            const target = this.groundAngle + s * (tail ? -this.grindAngles.tail : this.grindAngles.nose);
            const accel = GRIND.stiffness * wrapAngle(target - frame.GetAngle()) - GRIND.damping * frame.GetAngularVelocity();
            frame.ApplyTorque(this.boardInertia * clamp(accel, -GRIND.maxAccel, GRIND.maxAccel), true);
        }

        let back = lerp(STANCE.back, tail ? STANCE.tail : GRIND.noseBack, this.grind);
        let front = lerp(STANCE.front, tail ? GRIND.tailFront : STANCE.nose, this.grind);
        let crouch = POSE.rideCrouch + (GRIND.crouch - POSE.rideCrouch) * this.grind;
        let lean = POSE.rideLean;
        let arms = lerpArms(ARMS.ride, ARMS[tail ? 'tail' : 'nose'], this.grind);
        let crouchSpeed = CROUCH.speed;

        const velocity = frame.GetLinearVelocity();
        let velocityX = velocity.x;
        let velocityY = velocity.y;
        const ground = this.wheelGround.back || this.wheelGround.front;
        if (ground) {
            const carried = ground.body.GetLinearVelocityFromWorldPoint(frame.GetPosition());
            velocityX -= carried.x;
            velocityY -= carried.y;
        }
        const forward = s * (velocityX * Math.cos(frame.GetAngle()) + velocityY * Math.sin(frame.GetAngle()));
        this.tucked = this.accel * s > 0 && (forward > PUSH.tuckSpeed || (this.tucked && forward > PUSH.untuckSpeed));

        const pushing = this.accel != 0 && onGround && !this.tucked && this.charge === 0 && this.popTime < 0 && this.grind === 0;
        const towardsNose = this.accel * s > 0;
        const against = towardsNose ? -forward : forward;
        this.braking = pushing && (against > PUSH.brakeSpeed || (this.braking && against > PUSH.brakeSpeed - 1));
        let pushFoot = null;
        let pushShift = 0;
        if (this.braking) {
            pushFoot = PUSH.brake;
            crouch = POSE.brakeCrouch;
            lean = POSE.brakeLean;
            arms = ARMS.brake;
            this.frontGrip = PUSH.grip;
        } else if (pushing) {
            const push = towardsNose ? PUSH.forwards : PUSH.backwards;
            if (this.frontGrip === 1) this.pushPhase = 0;
            const lastPhase = this.pushPhase;
            const cycle = clamp(push.stroke / (Math.abs(forward) * PUSH.plant + 0.001), PUSH.minCycle, PUSH.cycle);
            this.pushPhase = (this.pushPhase + dt / cycle) % 1;
            if (lastPhase < PUSH.touchdown && this.pushPhase >= PUSH.touchdown) {
                const pitchOffset = 0.3;
                AudioManager.playSFX('skateboard_pedle', idleVolume, 1.0 + (Math.random()*pitchOffset - pitchOffset/2), frame.GetPosition())
            }
            pushFoot = loopPoint(push.path, this.pushPhase);
            const reach = Math.sin(Math.PI * clamp((this.pushPhase - 0.1) / 0.85, 0, 1)) ** 2;
            crouch = POSE.rideCrouch + (CROUCH.push - POSE.rideCrouch) * reach;
            lean = push.lean;
            pushShift = push.shift * reach;
            const xs = push.path.map(key => key[1]);
            const swing = clamp((pushFoot.x - Math.min(...xs)) / (Math.max(...xs) - Math.min(...xs)), 0, 1);
            arms = {left: [5 + 60 * swing, 30], right: [5 + 60 * (1 - swing), 40]};
            this.frontGrip = PUSH.grip;
        } else if (!onGround || this.popTime >= 0) {
            this.frontGrip = 1;
        } else {
            this.frontGrip = Math.min(1, this.frontGrip + dt / PUSH.gripRamp);
        }
        if (this.tucked && onGround && this.grind === 0) {
            crouch = POSE.speedCrouch;
            lean = POSE.speedLean;
            arms = ARMS.speed;
        }

        if (this.charge > 0) {
            const t = easeOut(this.charge);
            crouch = Math.max(crouch, CROUCH.max * t);
            back = lerp(back, STANCE.tail, t);
            lean = POSE.rideLean + (POSE.crouchLean - POSE.rideLean) * t;
            arms = lerpArms(arms, ARMS.crouch, t);
        }
        const popping = this.popTime >= 0 && this.popTime < JUMP.popTime;
        if (popping) {
            crouch = CROUCH.extend;
            crouchSpeed = CROUCH.popSpeed;
            back = STANCE.tail;
            front = lerp(STANCE.front, STANCE.nose, this.popTime / JUMP.popTime);
            lean = 0;
            arms = ARMS.pop;
        } else if (this.popTime >= 0) {
            const t = Math.min(1, (this.popTime - JUMP.popTime) / (JUMP.levelTime - JUMP.popTime));
            back = lerp(STANCE.tail, STANCE.back, t);
            front = lerp(STANCE.nose, STANCE.front, t);
        }
        if (this.popTime >= 0) {
            if (this.leanFrame === simulationFrame()) this.airControl = true;
            if (!this.airControl) {
                const error = wrapAngle(this.takeoffAngle + (popping ? this.noseUpAngle : 0) - frame.GetAngle());
                frame.ApplyTorque(this.boardInertia * (JUMP.levelStiffness * error - JUMP.levelDamping * frame.GetAngularVelocity()), true);
            }
        }
        if (!grounded && !grinding && this.airTime > JUMP.coyoteTime && !popping) {
            const gravity = frame.GetWorld().GetGravity();
            const rising = frame.GetLinearVelocity().x * gravity.x + frame.GetLinearVelocity().y * gravity.y < 0;
            crouch = Math.max(crouch, rising ? CROUCH.tuck : CROUCH.fall);
            arms = ARMS.air;
        }
        if (this.landTime >= 0) {
            const t = 1 - this.landTime / CROUCH.landTime;
            crouch = Math.max(crouch, this.landDepth * t);
            arms = lerpArms(arms, ARMS.land, t);
            this.landTime += dt;
            if (this.landTime > CROUCH.landTime) this.landTime = -1;
        }

        const crouchStep = crouchSpeed * dt;
        this.crouch += clamp(crouch - this.crouch, -crouchStep, crouchStep);
        this.torsoLean += clamp(lean - this.torsoLean, -POSE.leanSpeed * dt, POSE.leanSpeed * dt);
        let tilt = (tail ? -this.grindAngles.tail : this.grindAngles.nose) * this.grind * GRIND.upright[this.grindSide];
        let pivot = tail ? this.pivots.tail : this.pivots.nose;
        if (this.popTime >= 0 && !this.airControl) {
            tilt = clamp(s * wrapAngle(frame.GetAngle() - this.takeoffAngle), -0.6, 0.6) * JUMP.upright;
            pivot = {x: 0, y: 0};
        }
        this.pushShift += clamp(pushShift - this.pushShift, -CROUCH.speed * dt, CROUCH.speed * dt);
        this.setSupport(this.crouch, (tail ? -GRIND.shift : GRIND.shift) * this.grind + this.pushShift, this.torsoLean, tilt, pivot);

        const footAngle = frame.GetAngle();
        const deckPoint = target => {
            vec1.Set(s * target.x, target.y);
            const point = frame.GetWorldPoint(vec1);
            return {x: point.x, y: point.y};
        };
        const freeFoot = this.frontGrip < 1;
        if (freeFoot) {
            const target = this.withinReach('front', deckPoint(pushFoot || STANCE.front), footAngle);
            vec1.Set(target.x, target.y);
            const local = frame.GetLocalPoint(vec1);
            front = {x: s * local.x, y: local.y};
        }
        this.feet = {back, front};
        this.applyGrips();
        const pushFlex = PUSH_ANKLE_FLEX + (ANKLE_FLEX - PUSH_ANKLE_FLEX) * this.frontGrip;
        this.joint('feet_right_joint')?.SetLimits(-pushFlex, pushFlex);

        // A loose foot lags its target; driving its leg there would pull the ankle apart.
        const foot = this.lookupObject[Humanoid.BODY_PARTS.FEET_RIGHT].GetPosition();
        this.driveLeg('back', ...Object.values(deckPoint(back)), footAngle);
        if (freeFoot) this.driveLeg('front', foot.x, foot.y, footAngle);
        else this.driveLeg('front', ...Object.values(deckPoint(front)), footAngle);
        this.driveArms(arms, 0.25 * this.crouch / CROUCH.max);
    }
    releaseRider() {
        this.releaseMotors();
        const rider = this.character.lookupObject;
        ['back', 'front'].forEach(side => {
            const joint = this.grip(side);
            if (joint) game.editor.DestroyJoint(joint);
            delete rider[GRIPS[side]];
        });
        this.setAnkleFlex(0);
        this.jumpHeld = false;
        this.charge = 0;
        this.popTime = -1;
    }
    eject() {
        super.eject();
        this.releaseRider();
    }
    bail() {
        this.character.detachFromVehicle();
        vec1.Set(this.bailVelocity.x, this.bailVelocity.y);
        this.character.lookupObject._bodies.forEach(body => {
            if (!body.snapped) body.SetLinearVelocity(vec1);
        });
    }
    update() {
        super.update();

        const wheelRotationSpeed = (Math.abs(this.lookupObject.wheel_back.GetAngularVelocity())/20 + Math.abs(this.lookupObject.wheel_front.GetAngularVelocity())/20)/2;
        let idleVolume = Math.min(wheelRotationSpeed/15, 0.1);
        if(idleVolume< 0.002) idleVolume = 0;

        this.updateDeck();
        this.bailHits.push(this.bailImpulse / this.boardMass);
        if (this.bailHits.length > BAIL.steps) this.bailHits.shift();
        this.bailImpulse = 0;
        const hit = this.bailHits.reduce((sum, speed) => sum + speed, 0);
        if (this.character.attachedToVehicle && hit > BAIL.speed) this.bail();
        else if (hit === 0) {
            const velocity = this.lookupObject.frame.GetLinearVelocity();
            this.bailVelocity = {x: velocity.x, y: velocity.y};
        }
        if(this.character.alive && this.character.attachedToVehicle) this.updateRider(idleVolume);
        else this.grind = 0;
        this.updateSparks();

        AudioManager.playPrefabUniqueLoopSFX(this.prefabObject.key, 'skateboard_idle_loop', idleVolume, Math.max(0.8, wheelRotationSpeed * 0.5), this.lookupObject.frame.GetPosition());

    }
    destroy(){
        AudioManager.stopPrefabUniqueLoopSFX(this.prefabObject.key, 'skateboard_idle_loop');
        AudioManager.stopPrefabUniqueLoopSFX(this.prefabObject.key, GRIND.sound);
        if (this.sparks) {
            emitterManager.destroyEmitter(this.sparks);
            this.sparks = null;
        }
        super.destroy();
    }
    initContactListener() {
        super.initContactListener();
        const self = this;
        this.contactListener.PreSolve = function(contact) {
            // A grinding deck end digs in and bounces the board off at normal friction.
            const bodyA = contact.GetFixtureA().GetBody();
            const bodyB = contact.GetFixtureB().GetBody();
            if ((bodyA === self.lookupObject.frame || bodyB === self.lookupObject.frame) && !(bodyA.mainCharacter && bodyB.mainCharacter) && self.character.attachedToVehicle) {
                contact.SetFriction(GRIND.friction);
                contact.SetRestitution(0);
            }
            // The push is applied to the board; a planted foot dragged at board speed tears the ankle.
            const foot = self.lookupObject[Humanoid.BODY_PARTS.FEET_RIGHT];
            if (self.frontGrip < 1 && (bodyA === foot || bodyB === foot) && !(bodyA.mainCharacter && bodyB.mainCharacter)) contact.SetFriction(PUSH.friction);
            // disable collision with right leg
            const skateboardPartsToIgnore = [self.lookupObject.frame, self.lookupObject.wheel_front, self.lookupObject.wheel_back];
            if(skateboardPartsToIgnore.includes(contact.GetFixtureA().GetBody()) || skateboardPartsToIgnore.includes(contact.GetFixtureB().GetBody())){
                const other = skateboardPartsToIgnore.includes(contact.GetFixtureA().GetBody()) ? contact.GetFixtureB().GetBody() : contact.GetFixtureA().GetBody();
                if(other.mainCharacter && other.mySprite && ["feet_right", 'leg_right', 'thigh_right'].includes(other.mySprite.data.refName)){
                    contact.SetEnabled(false);
                }
            }
        }
        this.contactListener.PostSolve = function(contact, impulse) {
            if (!self.character.attachedToVehicle || contact.GetFixtureA().IsSensor() || contact.GetFixtureB().IsSensor()) return;
            const bodyA = contact.GetFixtureA().GetBody();
            const bodyB = contact.GetFixtureB().GetBody();
            const board = [self.lookupObject.frame, self.lookupObject.wheel_back, self.lookupObject.wheel_front];
            if (!board.includes(bodyA) && !board.includes(bodyB)) return;
            if ((board.includes(bodyA) ? bodyB : bodyA).mainCharacter) return;
            contact.GetWorldManifold(worldManifold);
            const normal = worldManifold.get_normal();
            const angle = self.lookupObject.frame.GetAngle();
            const along = Math.abs(normal.x * Math.cos(angle) + normal.y * Math.sin(angle));
            if (along < BAIL.axis) return;
            for (let i = 0; i < impulse.get_count(); i++) self.bailImpulse += impulse.get_normalImpulses(i) * along;
        }
    }
}

PrefabManager.prefabLibrary.Skateboard = {
    json: '{"objects":[[0,0.0056,3.4474,0,"skateboard","frame",0,["#999999"],["#000"],[0],false,true,[[{"x":2.278,"y":-0.177},{"x":2.278,"y":0.177},{"x":-2.278,"y":0.177},{"x":-2.278,"y":-0.177}]],[8],[7],[0],"",[1],true,false,false,[0],[0.2],false,true,false,false],[1,0.2707,107.591,0,"","",1,"SkateBoard_Board0000",0,4.17,-1.546,0,false,"#FFFFFF",1,1,1,0,0,0,true],[0,1.5441,3.9684,0,"skateboard","wheel_front",2,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[30],[7],[9.707],"",[1],true,false,false,[0.3],[0.2],false,true,false,false],[1,46.7256,119.2153,0,"","",3,"Skateboard_Wheel0000",2,0.435,-0.384,0,false,"#FFFFFF",1,1,1,0,0,0,true],[0,-1.5214,3.9724,0,"skateboard","wheel_back",4,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[30],[7],[9.707],"",[1],true,false,false,[0.3],[0.2],false,true,false,false],[1,-45.2394,119.3353,0,"","",5,"Skateboard_Wheel0000",4,0.435,-0.384,0,false,"#FFFFFF",1,1,1,0,0,0,true],[0,-0.0864,0.7924,-0.401,".character#Character , .flesh","thigh_left",6,["#999999"],["#000"],[0],false,true,[[{"x":-0.202,"y":-1.031},{"x":0.196,"y":-1.044},{"x":0.17,"y":1.038},{"x":-0.164,"y":1.038}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[0,-0.3124,2.3624,0.768,".character#Character , .flesh","leg_left",7,["#999999"],["#000"],[0],false,true,[[{"x":-0.161,"y":-0.912},{"x":0.161,"y":-0.925},{"x":0.084,"y":0.912},{"x":-0.084,"y":0.925}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,-2.7517,24.1535,-0.401,"","",8,"Normal_Thigh0000",6,0.413,-2.367,0,true,"#FFFFFF",1,1,1,0,0,0,true],[0,-0.7784,3.2204,0.035,".character#Character , .flesh","feet_left",9,["#999999"],["#000"],[0],false,true,[[{"x":-0.353,"y":-0.233},{"x":0.359,"y":0},{"x":0.359,"y":0.123},{"x":-0.365,"y":0.111}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,-9.5158,70.734,0.768,"","",10,"Normal_Leg0000",7,0.199,-3.142,0,true,"#FFFFFF",1,1,1,0,0,0,true],[1,-22.7742,95.2121,0.035,"","",11,"Normal_Feet0000",9,1.515,1.214,0,true,"#FFFFFF",1,1,1,0,0,0,true],[0,0.2656,-2.3076,0.052,".character#Character , .flesh","shoulder_left",12,["#999999"],["#000"],[0],false,true,[[{"x":-0.185,"y":-0.859},{"x":0.193,"y":-0.842},{"x":0.111,"y":0.851},{"x":-0.119,"y":0.851}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[0,0.3466,-0.7796,-0.175,".character#Character , .flesh","arm_left",13,["#999999"],["#000"],[0],false,true,[[{"x":-0.136,"y":-0.686},{"x":0.144,"y":-0.703},{"x":0.127,"y":0.694},{"x":-0.136,"y":0.694}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,8.5273,-69.1066,0.052,"","",14,"Normal_Shoulder0000",12,0.573,-0.161,0,true,"#FFFFFF",1,1,1,0,0,0,true],[1,10.6215,-23.1914,-0.175,"","",15,"Normal_Arm0000",13,0.298,-0.894,0,true,"#FFFFFF",1,1,1,0,0,0,true],[0,0.3596,0.0924,-0.018,".character#Character , .flesh","hand_left",16,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[1],[7],[7.513],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,9.0301,2.3912,-0.018,"","",17,"Normal_Hand0000",16,1.798,2.91,0,true,"#FFFFFF",1,1,1,0,0,0,true],[0,-0.3644,-0.3176,-0.14,".character#Character , .flesh","belly",18,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[1],[7],[14.181],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[0,0.3566,-4.6106,-0.14,".character#Character , .flesh","head",19,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[1],[7],[30.393],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[0,-0.0254,-2.0846,0.209,".character#Character , .flesh","body",20,["#999999"],["#000"],[0],false,true,[[{"x":-0.537,"y":1.202},{"x":-0.432,"y":-1.37},{"x":-0.15,"y":-1.828},{"x":0.132,"y":-1.793},{"x":0.555,"y":-1.123},{"x":0.555,"y":1.308},{"x":0.097,"y":1.801},{"x":-0.22,"y":1.801}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,-8.5761,-17.9513,0.06,"","",21,"Normal_Belly0000",18,8.747,1.158,-0.2,false,"#FFFFFF",1,1,1,0,0,0,true],[1,0.8446,-66.3844,0.209,"","",22,"Normal_Core0000",20,4.169,1.384,0,true,"#FFFFFF",1,1,1,0,0,0,true],[1,12.4968,-140.7056,-0.14,"","",23,"Normal_Head_Idle0000",19,2.99,0.785,0,true,"#FFFFFF",1,1,1,0,0,0,true],[0,0.1266,0.6794,-0.663,".character#Character , .flesh","thigh_right",24,["#999999"],["#000"],[0],false,true,[[{"x":-0.202,"y":-1.031},{"x":0.196,"y":-1.044},{"x":0.17,"y":1.038},{"x":-0.164,"y":1.038}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[0,0.3956,2.2504,0.437,".character#Character , .flesh","leg_right",25,["#999999"],["#000"],[0],false,true,[[{"x":-0.161,"y":-0.912},{"x":0.161,"y":-0.925},{"x":0.084,"y":0.912},{"x":-0.084,"y":0.925}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[0,0.5736,-4.7316,-0.14,".character#Character","eye_left",26,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[1],[7],[7.534],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,3.7424,20.7916,-0.663,"","",27,"Normal_Thigh0000",24,0.413,-2.367,0,true,"#FFFFFF",1,1,1,0,0,0,true],[1,11.687,67.428,0.437,"","",28,"Normal_Leg0000",25,0.199,-3.142,0,true,"#FFFFFF",1,1,1,0,0,0,true],[1,17.3004,-142.5009,-0.14,"","",29,"Normal_Eye0000",26,0.561,1.264,0,null,"#FFFFFF",1,1,1,0,0,0,true],[0,0.2656,3.2364,0.034,".character#Character , .flesh","feet_right",30,["#999999"],["#000"],[0],false,true,[[{"x":-0.353,"y":-0.233},{"x":0.359,"y":0},{"x":0.359,"y":0.123},{"x":-0.365,"y":0.111}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,8.5444,95.6915,0.034,"","",31,"Normal_Feet0000",30,1.515,1.214,0,true,"#FFFFFF",1,1,1,0,0,0,true],[0,0.1236,-2.1506,0.035,".character#Character , .flesh","shoulder_right",32,["#999999"],["#000"],[0],false,true,[[{"x":-0.185,"y":-0.859},{"x":0.193,"y":-0.842},{"x":0.111,"y":0.851},{"x":-0.119,"y":0.851}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[0,0.1526,-0.6266,-0.053,".character#Character , .flesh","arm_right",33,["#999999"],["#000"],[0],false,true,[[{"x":-0.136,"y":-0.686},{"x":0.144,"y":-0.703},{"x":0.127,"y":0.694},{"x":-0.136,"y":0.694}]],[1],[7],[null],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[0,1.2866,-4.8266,-0.14,".character#Character","eye_right",34,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[1],[7],[7.534],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,38.6904,-145.3509,-0.14,"","",35,"Normal_Eye0000",34,0.561,1.264,0,null,"#FFFFFF",1,1,1,0,0,0,true],[1,4.2693,-64.4061,0.035,"","",36,"Normal_Shoulder0000",32,0.573,-0.161,0,true,"#FFFFFF",1,1,1,0,0,0,true],[1,4.776,-18.5756,-0.053,"","",37,"Normal_Arm0000",33,0.298,-0.894,0,true,"#FFFFFF",1,1,1,0,0,0,true],[0,0.0366,0.2594,-0.087,".character#Character , .flesh","hand_right",38,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[1],[7],[7.513],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[1,-0.682,7.5232,-0.087,"","",39,"Normal_Hand0000",38,1.798,2.91,0,true,"#FFFFFF",1,1,1,0,0,0,true],[0,0.3596,-0.5526,0,"skateboard","stabalizer",40,["#999999"],["#000"],[0],false,true,[[{"x":0,"y":0},{"x":0,"y":0}]],[0.6],[2],[34.639],"",[1],true,false,false,[0.5],[0.2],false,true,false,false],[2,9.0658,52.3899,-0.035,".character#Character","leg_left_joint",41,7,6,0,false,false,1,10,true,0,-149,0,0,0,0,false],[2,-30.8172,88.3629,-0.279,".character#Character","feet_left_joint",42,9,7,0,false,false,1,10,true,0,0,0,0,0,0,false],[2,7.9218,-45.3861,-0.14,".character#Character","arm_left_joint",43,13,12,0,false,false,1,10,true,152,0,0,0,0,0,false],[2,14.8478,-4.7521,1.483,".character#Character","hand_left_joint",44,16,13,0,false,false,1,10,true,60,-60,0,0,0,0,false],[2,-5.4182,-1.3341,-0.262,".character#Character","thigh_left_joint",45,6,18,0,false,false,1,10,true,142,-16,0,0,0,0,false],[2,9.4788,-92.4481,-0.14,".character#Character","shoulder_left_joint",46,12,20,0,false,false,1,10,true,180,-19,0,0,0,0,false],[2,11.2528,-110.1941,-0.14,".character#Character","head_joint",47,19,20,0,false,false,1,10,true,58,-64,0,0,0,0,false],[2,23.2078,42.8949,0.227,".character#Character","leg_right_joint",48,25,24,0,false,false,1,10,true,-10,-149,0,0,0,0,false],[2,1.5728,89.0959,-0.296,".character#Character","feet_right_joint",49,30,25,0,false,false,1,10,true,0,0,0,0,0,0,false],[2,36.3848,-144.0251,-0.14,".character#Character","eye_right_joint",50,34,19,0,false,false,1,10,true,0,0,0,0,0,0,false],[2,3.3528,-39.9551,-0.14,".character#Character","arm_right_joint",51,33,32,0,false,false,1,10,true,152,0,0,0,0,0,false],[2,4.6898,0.7159,1.658,".character#Character","hand_right_joint",52,38,33,0,false,false,1,10,true,60,-60,0,0,0,0,false],[2,4.6698,-87.1121,-0.14,".character#Character","shoulder_right_joint",53,32,20,0,false,false,1,10,true,180,-19,0,0,0,0,false],[2,15.7538,-142.1271,-0.14,".character#Character","eye_left_joint",54,26,19,0,false,false,1,10,true,0,0,0,0,0,0,false],[2,-9.7812,-15.4741,-0.14,".character#Character","belly_joint",55,20,18,0,false,false,1,10,true,10,-10,0,0,0,0,false],[2,-11.9752,-0.0291,-0.262,".character#Character","thigh_right_joint",56,24,18,0,false,false,1,10,true,142,-16,0,0,0,0,false],[2,-32.9822,98.1909,0,".character#Character","foot_left_d1",57,0,9,0,false,false,1,10,false,0,0,0.3,10,0,0,true],[2,-54.4912,34.2069,0,".character#Character","back_stand_joint",58,20,0,2,false,false,1,10,false,0,0,0.5,4,0,0,true],[2,49.9778,31.8389,0,".character#Character","front_stand_joint",59,20,0,2,false,false,1,10,false,0,0,0.5,4,0,0,true],[2,10.2328,-16.5671,0,"skateboard","stabalizer_joint",60,40,0,0,false,false,1,10,false,0,0,0,0,0,0,true],[2,-108.3572,-100.1641,0,".character#Character","back_stabalize_joint",61,20,40,2,false,false,1,10,false,0,0,0.3,3,0,0,true],[2,-88.2312,-151.6631,0,".character#Character","neck_stabalize_joint",62,19,40,2,false,false,1,10,false,0,0,0.5,4,0,0,true],[2,-15.5302,98.9099,0,".character#Character","foot_left_d2",63,0,9,0,false,false,1,10,false,0,0,0.3,10,0,0,true],[2,-25.8972,95.8979,0,".character#Character","foot_left_rope",64,9,4,3,false,false,1,10,false,0,0,0,0,0,0,true],[2,-46.1012,119.0129,0,"skateboard","engine1",65,4,0,0,false,true,1,10,false,0,0,0,0,0,0,true],[2,45.8988,119.0129,0,"skateboard","engine2",66,2,0,0,false,true,1,10,false,0,0,0,0,0,0,true]]}',
    class: Skateboard,
    library: PrefabManager.LIBRARY_ADMIN,
}
